import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Collapse,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DateTime } from "luxon";
import { Clipboard } from "react-feather";
import { Link, useParams } from "react-router-dom";

import { SettingValue } from "@safetyculture/s12-apis-connect-web/s12/accounts/settings/v1/settings_pb";
import { SubscriptionSeatTypes } from "@safetyculture/s12-apis-connect-web/s12/common/seat_types_pb";
import { SortDirection } from "@safetyculture/s12-apis-connect-web/s12/common/sorting_pb";
import { OrgSsoSettings } from "@safetyculture/s12-apis-connect-web/s12/identity/admin/v1/idp_pb";
import { GetPaymentMethodsResponse } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/admin/v1/subscriptions_pb";
import { BillingProvider } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/billing_provider_pb";
import { ZuoraMigrationStatus } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/subscriptions_pb";
import {
  GetOrgInspectionQuotaResponse,
  RoleType,
  Subscription,
  SubscriptionType,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";
import {
  GetOrganisationUserListResponse,
  Pagination,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";
import {
  UserSortType,
  UserStatus,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_user_pb";

import { IdValue } from "../components/org-display/dashboard/cards/card-shell";
import OrgActionsMenu from "../components/org-display/dashboard/org-actions-menu";
import OrgDashboard from "../components/org-display/dashboard/org-dashboard";
import SummaryStatusPills from "../components/org-display/dashboard/summary-status-pills";
import OrgDetails from "../components/org-display/details-display-cards/org-details";
import SubscriptionDetails from "../components/org-display/details-display-cards/subscription-details";
import SubscriptionDetailsZuora from "../components/org-display/details-display-cards/subscription-details-zuora";
import DataGovernanceTab from "../components/org-display/tabs/data-governance-tab";
import OrgContractorsTab from "../components/org-display/tabs/org-contractors-tab";
import OrgContractTab from "../components/org-display/tabs/org-contracts-tab";
import OrgGroupsTab from "../components/org-display/tabs/org-groups-tab";
import OrgLoneWorkerSettingsTab from "../components/org-display/tabs/org-loneworker-settings-tab";
import OrgSegmentsTab from "../components/org-display/tabs/org-segments-tab";
import OrgUsersTab from "../components/org-display/tabs/org-users-tab";
import SubscriptionsTab from "../components/org-display/tabs/subscriptions-tab";
import SubscriptionsTabZuora from "../components/org-display/tabs/subscriptions-tab-zuora";
import TrainingDataGovernanceTab from "../components/org-display/tabs/training-data-governance-tab";
import TabPanel from "../components/shared/tab-panel";
import { DocPrefix, NO_DISPLAY_DATA } from "../constants/index";
import downloadFile from "../helpers/download-file";
import generateUserListCSV from "../helpers/generate-user-list-csv";
import useDebouncedEffect from "../helpers/hooks/use-debounced-effect";
import useNavRedesign from "../helpers/hooks/use-nav-redesign";
import { toS12ID, toUUID } from "../helpers/s12-helpers";
import { convertTimestampToDate, formatDateTimeDisplay } from "../helpers/time";
import { UrlParamTypes } from "../types/org-display";
import { getOrgContractList } from "api/endpoints/contracts";
import {
  GetUsersListParams,
  getOrgDetails,
  getOrgUserList,
  getOrgWithSubscription,
  getSubscription,
  listOrganisationGroups,
  listUsersLastSeen,
} from "api/endpoints/organisation";
import { OrgScimData, getOrgScimStatus } from "api/endpoints/scim";
import { SsoAdminData, getOrgsForConnectionName } from "api/endpoints/sso";
import {
  DeletionStatus,
  getPaymentMethods,
  getSubscriptionSeatUsage,
} from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import ChargifyLogoSvg from "assets/chargify-logo";
import ModuleSubscriptionsTab from "components/org-display/tabs/module-subscriptions-tab";
import OrgAITab from "components/org-display/tabs/org-ai-tab";
import OrgAssetsTab from "components/org-display/tabs/org-assets-tab";
import OrgContentLibraryTab from "components/org-display/tabs/org-content-library-tab";
import IntegrationsTab from "components/org-display/tabs/org-integrations-tab";
import OrgManufacturingTab from "components/org-display/tabs/org-manufacturing-tab";
import OrgNotificationsTab from "components/org-display/tabs/org-notifications-tab";
import SSOTab from "components/org-display/tabs/org-sso-tab";
import OrgTrainingAppFeaturesTab from "components/org-display/tabs/org-training-app-feature-tab";
import OrgTrainingCoursesTab from "components/org-display/tabs/org-training-courses-tab";
import WebhooksTab from "components/org-display/tabs/org-webhooks-tab";
import TemplateSearch from "components/search/template-search";
import RestrictionsTab from "components/shared/restrictions/restrictions-tab";
import { FeatureFlagsContext } from "context/feature-flags-context";
import {
  AdminOnlyFilterContext,
  ContractsListContext,
  IsOrgAndSeatDataLoadingContext,
  OrgDataContext,
  OrgDetailsContext,
  OrgHasSensorsContext,
  OrgInspectionQuotaContext,
  OrgUsersListContext,
  SubDataContext,
  SubscriptionSeatUsageContext,
  UserQueryFilterContext,
  UserSeatTypeFilterContext,
  UserStatusFilterContext,
} from "context/organisation-context";
import { PaymentMethodsContext } from "context/payment-methods-context";
import { GrpcError, SnackbarContext } from "context/snackbar-context";
import { getEnvironment } from "helpers/environment";
import { isUnderZuoraMigration } from "helpers/subscriptions";

const Organisation: React.FC = () => {
  const theme = useTheme();
  const [chosenTab, setTabValue] = useState(0);
  const [collapsedGroups, setCollapsedGroups] = useState<
    Record<string, boolean>
  >({});
  const navRedesign = useNavRedesign();
  const [showDetails, setShowDetails] = useState(true);
  const [customisingLayout, setCustomisingLayout] = useState(false);

  // Card padding and scrolling now belong to the dashboard's own cards; the
  // classic panes still bring their own.
  const defaultViewSx = {
    height: 650,
  };
  const classicPaneSx = {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
    ...defaultViewSx,
  };
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const { orgData, setOrgData } = useContext(OrgDataContext);
  const { orgDetails, setOrgDetails } = useContext(OrgDetailsContext);
  const { subData, setSubData } = useContext(SubDataContext);
  const {
    setContractsList,
    setIsContractListLoading,
    isContractListLoading,
    setContractListError,
  } = useContext(ContractsListContext);
  const [underMigration, setUnderMigration] = useState(false);
  const { setOrgHasSensors } = useContext(OrgHasSensorsContext);
  const { isOrgAndSeatDataLoading, setIsOrgAndSeatDataLoading } = useContext(
    IsOrgAndSeatDataLoadingContext,
  );
  const { subscriptionSeatUsage, setSubscriptionSeatUsage } = useContext(
    SubscriptionSeatUsageContext,
  );
  const [ssoData, setSsoData] = useState<SsoAdminData>();
  const [scimData, setScimData] = useState<OrgScimData>();
  const [orgSettings, setOrgSettings] = useState<SettingValue[]>([]);
  const { orgUsersList, setOrgUsersList } = useContext(OrgUsersListContext);
  const { adminOnlyFilter } = useContext(AdminOnlyFilterContext);
  const { userQueryFilter } = useContext(UserQueryFilterContext);
  const { userStatusFilter } = useContext(UserStatusFilterContext);
  const { userSeatTypeFilter } = useContext(UserSeatTypeFilterContext);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [userExportProgress, setUserExportProgress] = useState({
    current: 0,
    total: 0,
  });
  const [isSuspended, setIsSuspended] = useState(false);
  const { setFeatureFlags } = useContext(FeatureFlagsContext);
  const [isFeatureFlagsLoading, setIsFeatureFlagsLoading] = useState(false);
  const {
    setPaymentMethods,
    setDefaultPaymentMethod,
    setDefaultPaymentMethodDetails,
  } = useContext(PaymentMethodsContext);
  const [isPaymentMethodsLoading, setIsPaymentMethodsLoading] = useState(false);
  const { setOrgInspectionQuota } = useContext(OrgInspectionQuotaContext);

  // Initialize a ref to track the initial render
  const isFirstRender = useRef(true);
  const { showError, copyToClipboard } = useContext(SnackbarContext);

  const getOrganisationUserList = (
    roleId: string,
    pageSize: number,
    offset: number,
    adminOnly: boolean,
    userQuery: string,
    userStatus: UserStatus,
    seatType: SubscriptionSeatTypes,
  ) => {
    let updatedUsersList = {
      ...orgUsersList,
    };
    updatedUsersList.isLoading = true;
    setOrgUsersList(updatedUsersList);
    let includeInactiveRoles = false;
    if (userStatus == UserStatus.UNSPECIFIED) {
      includeInactiveRoles = true;
    }

    const payload: GetUsersListParams = {
      organizationId: roleId,
      pageSize,
      offset,
      sortDirection: SortDirection.ASC,
      sortType: UserSortType.FIRST_NAME,
      adminOnly,
      query: userQuery,
      userStatus,
      seatType,
      includeInactiveRoles,
    };

    getOrgUserList(payload, callOptionsHeaders(regionCode, roleId))
      .then((res) => {
        updatedUsersList = {
          ...orgUsersList,
          usersListResponse: res,
          pagination: new Pagination({ pageSize: pageSize, offset: offset }),
        };
      })
      .catch(showError)
      .finally(() => {
        updatedUsersList.isLoading = false;
        setOrgUsersList(updatedUsersList);
      });
  };

  const exportUsersCsv = async () => {
    setExportingCsv(true);
    setUserExportProgress({ current: 0, total: 0 });

    let includeInactiveRoles = false;
    if (userStatusFilter == UserStatus.UNSPECIFIED) {
      includeInactiveRoles = true;
    }
    const orgLastSeenList = await listUsersLastSeen(
      {
        orgId: toUUID(roleId),
      },
      callOptionsHeaders(regionCode, roleId),
    );

    // Increased batch size for better performance
    const batchSize = 500;
    const getBatch = async (offset: number) => {
      const payload: GetUsersListParams = {
        organizationId: roleId,
        pageSize: batchSize,
        offset,
        sortDirection: SortDirection.ASC,
        sortType: UserSortType.FIRST_NAME,
        adminOnly: adminOnlyFilter,
        query: userQueryFilter,
        userStatus: userStatusFilter,
        seatType: userSeatTypeFilter,
        includeInactiveRoles: includeInactiveRoles,
      };

      return getOrgUserList(payload, callOptionsHeaders(regionCode, roleId))
        .then(async (batch) => {
          const batchWithLastSeen = batch.users.map((user) => {
            const userWithLastSeen = orgLastSeenList.users.find((lastSeen) => {
              return toS12ID(DocPrefix.PrefixUser, lastSeen.userId) === user.id;
            });
            if (userWithLastSeen) {
              user.lastSeen = userWithLastSeen.lastSeen;
            }
            return user;
          });

          batch.users = batchWithLastSeen;
          return batch;
        })
        .catch((e) => {
          showError(e);
          return new GetOrganisationUserListResponse({
            users: [],
            totalItems: 0,
          });
        });
    };

    const usersListInitialResp = await getBatch(0);
    const totalItems = usersListInitialResp.totalItems;
    setUserExportProgress({
      current: usersListInitialResp.users.length,
      total: totalItems,
    });

    let usersList = usersListInitialResp.users;
    for (let offset = batchSize; offset < totalItems; offset += batchSize) {
      const batch = await getBatch(offset);
      usersList = [...usersList, ...batch.users];
      setUserExportProgress({ current: usersList.length, total: totalItems });
    }

    const orgGroupsInfo = await listOrganisationGroups(
      {
        orgId: toUUID(roleId),
      },
      callOptionsHeaders(regionCode, roleId),
    );

    const fileName = `${orgData.name} - ${DateTime.now().toFormat(
      "d MMM yy",
    )}.csv`;
    const csv = generateUserListCSV(
      usersList,
      orgData.name,
      orgGroupsInfo.groups,
    );

    if (usersList.length > 0) {
      downloadFile(fileName, csv);
    }
  };

  const handleExportUsersCsv = () => {
    exportUsersCsv()
      .catch(showError)
      .finally(() => {
        setExportingCsv(false);
        setUserExportProgress({ current: 0, total: 0 });
      });
  };

  useEffect(() => {
    getOrganisationUserList(
      roleId,
      orgUsersList.pagination.pageSize,
      (orgUsersList.pagination.offset = 0),
      adminOnlyFilter,
      userQueryFilter,
      userStatusFilter,
      userSeatTypeFilter,
    );
    // Adding additional values to array will cause too many re-renders
    /* eslint-disable */
  }, [adminOnlyFilter, userStatusFilter, userSeatTypeFilter]);

  useDebouncedEffect(
    () => {
      // This is to skip the call on the initial render, the regular useEffect is already handling that.
      // This prevents a double api call on the initial render. This debounced effect is only for searching with the text field.
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }

      getOrganisationUserList(
        roleId,
        orgUsersList.pagination.pageSize,
        (orgUsersList.pagination.offset = 0),
        adminOnlyFilter,
        userQueryFilter,
        userStatusFilter,
        userSeatTypeFilter,
      );
    },
    1000,
    [userQueryFilter],
  );

  const makeOrganisationReq = async () => {
    setIsFeatureFlagsLoading(true);
    setIsPaymentMethodsLoading(true);
    setIsContractListLoading(true);
    setContractListError(false);

    const environment = getEnvironment();
    try {
      // The information in these calls (to soter and api-identity) are independent of each
      // other so should be done in parallel. Use Promise.allSettled instead of .all so page still loads
      // most of the org data even if the call to global identity fails.
      const orgDataValues = await Promise.allSettled([
        // 0
        getOrgWithSubscription(
          { roleId },
          callOptionsHeaders(regionCode, roleId),
        ),
        // 1
        getOrgDetails(
          { roleId, environment },
          callOptionsHeaders(regionCode, roleId),
        ),
        // 2
        getPaymentMethods(
          {
            organizationId: toUUID(roleId),
            filters: { deletionStatus: DeletionStatus.EXCLUDE_SOFT_DELETED },
          },
          callOptionsHeaders(regionCode, roleId),
        ),
        // 3
        getSubscription(
          { organizationId: toUUID(roleId) },
          callOptionsHeaders(regionCode, roleId),
        ),
      ]);

      // The newOrgData is used while the endpoint redesign for the subscriptions half is being completed
      // such that most of the org info is pulled from the new getOrgDetails call while the subscription
      // information is populated by the (soon to be deprecated) getOrgWithSubscription call.
      // Associated Jira ticket: https://safetyculture.atlassian.net/browse/BSA-9482
      let newOrgData = orgData;
      let newOrgDetails = orgDetails;
      if (orgDataValues[1].status === "fulfilled") {
        newOrgDetails = orgDataValues[1].value.roleDetails ?? orgDetails;
        newOrgData = orgDataValues[1].value.roleDetails?.role ?? orgData;

        setOrgHasSensors(newOrgDetails.sensorsExist);
        setIsSuspended(!newOrgDetails.active);
        setUnderMigration(newOrgDetails.migrationInProgress);

        const flagResp = newOrgDetails.featureFlags;
        setFeatureFlags(flagResp?.flags ?? {});

        const seatQuota = newOrgDetails.inspectionQuota;
        setOrgInspectionQuota(
          new GetOrgInspectionQuotaResponse({ inspectionQuota: seatQuota }),
        );

        // Extract organization settings
        const settings = newOrgDetails.orgSettings;
        setOrgSettings(settings?.values ?? []);

        // Extract SSO data from GetOrgDetails — ssoConfiguration is
        // GetSsoConfigurationAdminResponse which contains authConf directly.
        const ssoConfig = newOrgDetails.ssoConfiguration;
        const authConf = ssoConfig?.authConf;
        let otherOrgSettings: OrgSsoSettings[] = [];
        if (authConf?.ssoConf?.connDetails?.connectionName) {
          const otherOrgSettingsResp = await getOrgsForConnectionName(
            { connectionName: authConf.ssoConf.connDetails.connectionName },
            callOptionsHeaders(regionCode, roleId),
          ).catch((err) => {
            console.error(err);
            return { orgs: [] as OrgSsoSettings[] };
          });
          otherOrgSettings = otherOrgSettingsResp.orgs;
        }
        setSsoData({
          authConf,
          otherOrgSettings,
        });
      } else {
        setSsoData({ authConf: undefined, otherOrgSettings: [] });
      }

      getOrgScimStatus(
        { orgId: roleId },
        callOptionsHeaders(regionCode, roleId),
      )
        .then((status) => setScimData({ status, loadFailed: false }))
        .catch(() => setScimData({ loadFailed: true }));

      // This getOrgwithSubscription call is to be deprecated in favour of getOrgDetails
      // and a new endpoint to aggregate subscription details.
      if (orgDataValues[0].status === "fulfilled") {
        newOrgData.subscription = orgDataValues[0].value.role
          ?.subscription as Subscription[];
        newOrgDetails.role = newOrgData;

        if (
          orgDataValues[0].value.role?.type === RoleType.ORG &&
          orgDataValues[0].value.role?.subscription
        ) {
          await getOrgContractList(
            {
              orgId: roleId,
            },
            callOptionsHeaders(regionCode, roleId),
          )
            .then((contractsListResp) => {
              setContractsList(contractsListResp);
            })
            .catch((err) => {
              setContractListError(true);
              showError(err);
            })
            .finally(() => {
              setIsContractListLoading(false);
            });

          if (
            ![SubscriptionType.FREE, SubscriptionType.FREE_ORG].includes(
              orgDataValues[0].value.role?.subscription[0].type,
            )
          ) {
            await getSubscriptionSeatUsage(
              {
                subscriptionId: orgDataValues[0].value.role?.subscription[0].id,
                orgId: roleId,
              },
              callOptionsHeaders(regionCode, roleId),
            )
              .then((resp) => {
                setSubscriptionSeatUsage(resp);
              })
              .catch(showError)
              .finally(() => {
                setIsOrgAndSeatDataLoading(false);
              });
          }
        } else {
          // Contracts were never fetched for this org (not a RoleType.ORG, or it has no
          // subscription) - contractsList.contracts stays [], indistinguishable from "org
          // genuinely has none" unless callers also check this flag.
          setContractListError(true);
          setIsContractListLoading(false);
        }
      } else {
        // getOrgWithSubscription rejected, so the contracts fetch (nested inside this block)
        // never ran either.
        setContractListError(true);
        setIsContractListLoading(false);
      }
      if (orgDataValues[3].status === "fulfilled") {
        const resp = orgDataValues[3].value || subData;
        setSubData(resp);
      }
      if (orgDataValues[2].status == "fulfilled") {
        const resp = orgDataValues[2].value as GetPaymentMethodsResponse;
        setPaymentMethods(resp.paymentMethods);
        const paymentMethod =
          resp.paymentMethods.length > 0 ? resp.paymentMethods[0] : undefined;
        setDefaultPaymentMethod(resp.paymentMethods[0]);
        setDefaultPaymentMethodDetails(paymentMethod?.details?.details?.value);
      }

      setOrgData(newOrgData);
      setOrgDetails(newOrgDetails);
    } catch (e) {
      showError(e as GrpcError);
    }
    setIsOrgAndSeatDataLoading(false);
    setIsFeatureFlagsLoading(false);
    setIsPaymentMethodsLoading(false);
    setIsContractListLoading(false);
  };

  const getOrganisation = useCallback(makeOrganisationReq, [
    regionCode,
    roleId,
  ]);

  useEffect(() => {
    getOrganisation();
  }, [getOrganisation, regionCode, roleId]);

  const toggleGroup = (group: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  const handleTabChange = (
    _event: React.ChangeEvent<unknown>,
    newValue: number,
  ) => {
    setTabValue(newValue);
  };

  const isPremiumOrg = (subType: SubscriptionType) => {
    return (
      subType !== SubscriptionType.FREE && subType !== SubscriptionType.FREE_ORG
    );
  };

  const shouldDisableTab =
    isOrgAndSeatDataLoading || isFeatureFlagsLoading || isPaymentMethodsLoading;

  const tabs = [
    {
      label: "Users",
      component: (
        <OrgUsersTab
          handleOrgDataRefresh={getOrganisation}
          getOrganisationUserList={(
            roleId,
            pageSize,
            offset,
            adminOnly,
            userQuery,
            userStatus,
            seatType,
          ) => {
            getOrganisationUserList(
              roleId,
              pageSize,
              offset,
              adminOnly,
              userQuery,
              userStatus,
              seatType,
            );
          }}
          handleExportCsv={handleExportUsersCsv}
          isExportingCsv={exportingCsv}
          exportProgress={userExportProgress}
          isPremiumOrg={
            orgData.type === RoleType.ORG &&
            orgData.subscription.length > 0 &&
            isPremiumOrg(orgData.subscription[0].type)
          }
          isActiveSub={
            orgData.type === RoleType.ORG &&
            orgData.subscription.length > 0 &&
            orgData.subscription[0].active
          }
        />
      ),
      enabled: true,
      dataAnchor: "tab-users",
    },
    {
      label: "Groups",
      component: <OrgGroupsTab roleId={roleId} regionCode={regionCode} />,
      enabled: true,
      dataAnchor: "tab-groups",
    },
    {
      label: "Templates",
      component: <TemplateSearch orgID={orgData.id} />,
      enabled: true,
      dataAnchor: "tab-org-templates",
    },
    {
      label: "Subscription",
      component:
        subData?.subscription?.billingProvider === BillingProvider.ZUORA ? (
          <SubscriptionsTabZuora refreshOrgData={getOrganisation} />
        ) : (
          <SubscriptionsTab refreshOrgData={getOrganisation} />
        ),
      enabled: true,
      dataAnchor: "tab-subscription",
      disabled: shouldDisableTab,
    },
    {
      label: "Module Subscriptions",
      component: (
        <ModuleSubscriptionsTab
          roleId={roleId}
          regionCode={regionCode}
          refreshOrgData={getOrganisation}
        />
      ),
      enabled: true,
      dataAnchor: "tab-module-subscriptions",
    },
    {
      label: "Contracts",
      component: <OrgContractTab />,
      enabled: true,
      dataAnchor: "tab-contracts",
      disabled: isContractListLoading,
    },

    {
      label: "Contractors",
      component: <OrgContractorsTab roleId={roleId} regionCode={regionCode} />,
      enabled: true,
      dataAnchor: "tab-contractors",
    },
    {
      label: "Data Governance",
      component: (
        <DataGovernanceTab
          roleId={roleId}
          subscription={orgData.subscription && orgData.subscription[0]}
          orgName={orgData.name}
          numMembers={orgData.members?.length}
          numInactiveMembers={orgData.inactiveMembers?.length}
          regionCode={regionCode}
          roleFlags={orgData.orgFlags!}
        />
      ),
      enabled: true,
      dataAnchor: "tab-data-governance",
      disabled: shouldDisableTab,
    },
    {
      label: "SSO",
      component: (
        <SSOTab
          roleId={roleId}
          ssoData={ssoData}
          onRefresh={getOrganisation}
          regionCode={regionCode}
          orgName={orgData.name}
        />
      ),
      enabled: true,
      disabled: shouldDisableTab,
      dataAnchor: "tab-sso",
    },
    {
      label: "AI Usage",
      component: <OrgAITab roleId={roleId} regionCode={regionCode} />,
      enabled: true,
      dataAnchor: "tab-ai",
    },
    {
      label: "Segments",
      component: (
        <OrgSegmentsTab
          roleId={roleId}
          regionCode={regionCode}
          refreshOrgData={getOrganisation}
        />
      ),
      enabled: true,
      dataAnchor: "tab-segments",
    },
    {
      label: "Integrations",
      component: <IntegrationsTab roleId={roleId} regionCode={regionCode} />,
      enabled: true,
      dataAnchor: "tab-integrations",
    },
    {
      label: "Webhooks",
      component: <WebhooksTab roleId={roleId} regionCode={regionCode} />,
      enabled: true,
      dataAnchor: "tab-webhooks",
    },
    {
      label: "Assets",
      component: <OrgAssetsTab roleId={roleId} regionCode={regionCode} />,
      enabled: true,
      dataAnchor: "tab-assets",
    },
    {
      label: "Restrictions",
      component: <RestrictionsTab isUser={false} />,
      enabled: true,
      dataAnchor: "tab-restrictions",
    },
    {
      label: "Training Courses",
      component: (
        <OrgTrainingCoursesTab roleId={roleId} regionCode={regionCode} />
      ),
      enabled: orgDetails.isMigratedToScInfra,
      dataAnchor: "tab-training-courses",
    },
    {
      label: "Training App Features",
      component: (
        <OrgTrainingAppFeaturesTab roleId={roleId} regionCode={regionCode} />
      ),
      enabled: true,
      dataAnchor: "tab-training-app-features",
    },
    {
      label: "Training Data Governance",
      component: <TrainingDataGovernanceTab />,
      enabled: true,
      dataAnchor: "tab-training-data-governance",
    },
    {
      label: "Lone Worker",
      component: (
        <OrgLoneWorkerSettingsTab roleId={roleId} regionCode={regionCode} />
      ),
      enabled: true,
      dataAnchor: "tab-loneworker",
    },
    {
      label: "Content Library",
      component: (
        <OrgContentLibraryTab roleId={roleId} orgName={orgData.name} />
      ),
      enabled: true,
      dataAnchor: "tab-contentlibrary",
    },
    {
      label: "Notifications",
      component: (
        <OrgNotificationsTab roleId={roleId} regionCode={regionCode} />
      ),
      enabled: true,
      dataAnchor: "tab-org-notifications",
    },
    {
      label: "Manufacturing",
      component: (
        <OrgManufacturingTab roleId={roleId} regionCode={regionCode} />
      ),
      enabled: true,
      dataAnchor: "tab-manufacturing",
    },
  ].filter((tab) => tab.enabled);

  // Group the (otherwise flat) tab list into domains for the left sub-nav.
  // Tabs keep their original index so selection/content rendering is unchanged.
  // Keyed on the stable `dataAnchor` (not the human-facing label) so renaming a
  // tab's display text can't quietly drop it into the "Other" fallback.
  const tabGroups: { group: string; anchors: string[] }[] = [
    {
      group: "People & Access",
      anchors: ["tab-users", "tab-groups", "tab-contractors", "tab-sso"],
    },
    {
      group: "Commercial",
      anchors: [
        "tab-subscription",
        "tab-module-subscriptions",
        "tab-contracts",
        "tab-restrictions",
        "tab-ai",
      ],
    },
    {
      group: "Content",
      anchors: [
        "tab-org-templates",
        "tab-data-governance",
        "tab-assets",
        "tab-contentlibrary",
      ],
    },
    {
      group: "Integrations",
      anchors: [
        "tab-integrations",
        "tab-webhooks",
        "tab-segments",
        "tab-org-notifications",
      ],
    },
    {
      group: "Training",
      anchors: [
        "tab-training-courses",
        "tab-training-app-features",
        "tab-training-data-governance",
      ],
    },
    { group: "Industry", anchors: ["tab-loneworker", "tab-manufacturing"] },
  ];

  const groupedTabIndexes = new Set<number>();
  const navGroups = tabGroups
    .map(({ group, anchors }) => {
      const items = anchors
        .map((anchor) => ({
          tab: tabs.find((t) => t.dataAnchor === anchor),
          index: tabs.findIndex((t) => t.dataAnchor === anchor),
        }))
        .filter((item): item is { tab: (typeof tabs)[number]; index: number } =>
          Boolean(item.tab),
        );
      items.forEach((item) => groupedTabIndexes.add(item.index));
      return { group, items };
    })
    .filter((g) => g.items.length > 0);

  // Catch any tab not assigned to a group so nothing can silently disappear.
  const ungrouped = tabs
    .map((tab, index) => ({ tab, index }))
    .filter(({ index }) => !groupedTabIndexes.has(index));
  if (ungrouped.length > 0) {
    navGroups.push({ group: "Other", items: ungrouped });
  }

  // Tab content is identical across both layouts; only the navigation differs.
  const tabPanels = tabs.map((tab, index) => (
    <TabPanel
      key={tab.label}
      value={chosenTab}
      index={index}
      data-testid={`tab-${tab.label.toLowerCase()}`}
    >
      <Paper>{tab.component}</Paper>
    </TabPanel>
  ));

  // The detail area, laid out by the customisable dashboard (FG-8053).
  //
  // The redesign toggle governs the whole page, so turning it off puts the two
  // detail panes back exactly as they were - no stored card layout, no editing
  // chrome. Opting into the redesign is what opts into the cards.
  const detailCards = navRedesign ? (
    <OrgDashboard
      regionCode={regionCode}
      ssoData={ssoData}
      scimData={scimData}
      orgSettings={orgSettings}
      billingProvider={subData?.subscription?.billingProvider}
      refreshOrgData={getOrganisation}
      cardHeight={defaultViewSx.height}
      editing={customisingLayout}
      onEditingChange={setCustomisingLayout}
    />
  ) : (
    <>
      <Grid item xs={12} lg={6}>
        <Paper sx={classicPaneSx}>
          <OrgDetails
            regionCode={regionCode}
            ssoData={ssoData}
            scimData={scimData}
            orgSettings={orgSettings}
            refreshOrgData={getOrganisation}
          />
        </Paper>
      </Grid>
      <Grid item xs={12} lg={6}>
        <Paper sx={classicPaneSx}>
          {subData?.subscription?.billingProvider === BillingProvider.ZUORA ? (
            <SubscriptionDetailsZuora refreshOrgData={getOrganisation} />
          ) : (
            <SubscriptionDetails refreshOrgData={getOrganisation} />
          )}
        </Paper>
      </Grid>
    </>
  );

  const primarySub = orgData.subscription && orgData.subscription[0];
  const isZuora =
    subData?.subscription?.billingProvider === BillingProvider.ZUORA;

  const formatSeatUsage = (usage?: {
    occupiedSeats?: number;
    purchasedSeats?: number;
  }) => `${usage?.occupiedSeats ?? 0}/${usage?.purchasedSeats ?? 0}`;
  const seatBreakdown = `Full ${formatSeatUsage(
    subscriptionSeatUsage?.premiumSeatUsageSummary,
  )} · Lite ${formatSeatUsage(
    subscriptionSeatUsage?.liteSeatUsageSummary,
  )} · Free ${formatSeatUsage(
    subscriptionSeatUsage?.collaboratorSeatUsageSummary,
  )}`;
  const createdLabel = orgData.createdAt
    ? formatDateTimeDisplay(convertTimestampToDate(orgData.createdAt))
    : NO_DISPLAY_DATA;

  const factLabelSx = { color: "text.secondary" };
  const factValueSx = { color: "text.primary", fontWeight: 600 };
  const copiableSx = {
    ...factValueSx,
    cursor: "pointer",
    "&:hover": { textDecoration: "underline" },
  };

  const compactSummary = (
    // The strip is one line most of the time, so it gets tighter vertical
    // padding than a content card would.
    <Paper sx={{ px: 2, py: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {orgData.name || NO_DISPLAY_DATA}
        </Typography>
        {/* Tier and the three statuses, each marked with where it came from,
            so a Periscope-derived status is never read as a billing one. */}
        <SummaryStatusPills
          isZuora={isZuora}
          refreshOrgData={getOrganisation}
        />
        {!isZuora && (
          <Tooltip title="Chargify">
            <Box
              component="span"
              sx={{ display: "inline-flex", lineHeight: 0 }}
            >
              <ChargifyLogoSvg size={20} />
            </Box>
          </Tooltip>
        )}
        {/* Organisation-wide actions, which used to be reachable only from the
            detail pane's header kebab, plus the layout toggle. */}
        <Box sx={{ ml: "auto", display: "inline-flex" }}>
          <OrgActionsMenu
            regionCode={regionCode}
            orgSettings={orgSettings}
            onToggleCustomiseLayout={() => {
              const next = !customisingLayout;
              setCustomisingLayout(next);
              // Rearranging cards you cannot see does nothing, so asking to
              // customise implies opening them.
              if (next) setShowDetails(true);
            }}
            customisingLayout={customisingLayout}
          />
        </Box>
        <Button
          size="small"
          onClick={() => setShowDetails((prev) => !prev)}
          endIcon={showDetails ? <ExpandLess /> : <ExpandMore />}
          sx={{ textTransform: "none" }}
          data-testid="toggle-org-details"
        >
          {showDetails ? "Hide details" : "Full details"}
        </Button>
      </Box>
      {/* ID, data centre, created, owner and seats all appear in the cards
          below, so the strip only repeats them when the cards are hidden. */}
      <Box
        hidden={showDetails}
        sx={{
          display: showDetails ? "none" : "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "6px 24px",
          mt: 1,
        }}
      >
        <Box
          sx={{
            ...factLabelSx,
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            // Its own line: it is the fact people came for, and the rest read
            // as a group beneath it.
            flexBasis: "100%",
          }}
        >
          ID
          {/* The UUID form, as the cards show it, with the prefixed form behind
              the s12 chip. */}
          {orgData.id ? (
            <IdValue
              value={toUUID(orgData.id)}
              label="org UUID"
              onCopy={copyToClipboard}
              alternate={{ value: orgData.id, label: "s12" }}
              strong
              testIdPrefix="summary-id"
            />
          ) : (
            <Box component="span" sx={factValueSx}>
              {NO_DISPLAY_DATA}
            </Box>
          )}
        </Box>
        <Typography variant="body2" sx={factLabelSx}>
          Data centre{" "}
          <Box component="span" sx={factValueSx}>
            {regionCode.toUpperCase()}
          </Box>
        </Typography>
        <Typography variant="body2" sx={factLabelSx}>
          Created{" "}
          <Box component="span" sx={factValueSx}>
            {createdLabel}
          </Box>
        </Typography>
        <Typography variant="body2" sx={factLabelSx}>
          Owner{" "}
          {orgData.info?.email ? (
            <Box
              component="span"
              sx={copiableSx}
              onClick={() => copyToClipboard(orgData.info!.email)}
              data-testid="summary-owner-copy"
            >
              {orgData.info.email}{" "}
              <Clipboard size={12} style={{ verticalAlign: "middle" }} />
            </Box>
          ) : (
            <Box component="span" sx={factValueSx}>
              {NO_DISPLAY_DATA}
            </Box>
          )}
        </Typography>
        <Typography variant="body2" sx={factLabelSx}>
          Seats{" "}
          <Box component="span" sx={factValueSx}>
            {seatBreakdown}
          </Box>
        </Typography>
      </Box>
    </Paper>
  );

  // One item rather than two, so the strip and the cards are not separated by
  // the container's full spacing - and the strip sits closer to the page top.
  const headerSection = navRedesign ? (
    <Grid item xs={12} sx={{ pt: 1.5, pb: 0 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {compactSummary}
        <Collapse in={showDetails} timeout="auto" unmountOnExit>
          <Grid container spacing={2}>
            {detailCards}
          </Grid>
        </Collapse>
      </Box>
    </Grid>
  ) : (
    detailCards
  );

  return (
    <div>
      {isSuspended || underMigration ? (
        <Paper>
          <Alert
            severity="warning"
            sx={{ textAlign: "start", marginBottom: "10px" }}
            hidden={true}
          >
            <AlertTitle sx={{ textAlign: "start" }}>
              {" "}
              Warning - {underMigration ? "DC Migration" : "Suspended"}
            </AlertTitle>
            This organisation is{" "}
            {underMigration ? "under migration" : "currently suspended"}.{" "}
            <strong>
              {" "}
              {underMigration
                ? "Please contact #business-systems "
                : "Please raise a Finance DS ticket "}
              if you need to make changes to this organisation.
            </strong>
          </Alert>
        </Paper>
      ) : null}
      {orgDetails?.trainingMigrationInProgress && (
        <Paper>
          <Alert
            severity="warning"
            sx={{ textAlign: "start", marginBottom: "10px" }}
            hidden={true}
          >
            <AlertTitle sx={{ textAlign: "start" }}>
              Warning - Training Data Migration
            </AlertTitle>
            This organisation is currently undergoing a training data migration.
            Please contact #training-data-migration if you need to make changes
            to this organisation.
          </Alert>
        </Paper>
      )}
      {isUnderZuoraMigration(subData?.subscription?.zuoraMigrationStatus) && (
        <Paper>
          <Alert
            severity="warning"
            sx={{ textAlign: "start", marginBottom: "10px" }}
          >
            <AlertTitle sx={{ textAlign: "start" }}>
              Warning - Zuora Billing Migration
            </AlertTitle>
            This organisation is currently undergoing a Zuora billing migration
            {subData?.subscription?.zuoraMigrationStatus ===
            ZuoraMigrationStatus.COMPLETED_BILLING_OPS_PAUSED
              ? " (billing operations paused)"
              : " (in progress)"}
            . Billing changes may be rejected or cause unexpected issues during
            this period.{" "}
            <strong>
              Please avoid making billing changes unless absolutely necessary.
            </strong>
          </Alert>
        </Paper>
      )}
      <Grid container spacing={3}>
        {headerSection}
        {navRedesign ? (
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "flex-start",
                flexDirection: { xs: "column", md: "row" },
              }}
            >
              <Paper
                sx={{
                  width: { xs: "100%", md: 220 },
                  flexShrink: 0,
                  position: { md: "sticky" },
                  top: theme.spacing(2),
                  overflow: "hidden",
                }}
              >
                <List dense disablePadding component="nav">
                  {navGroups.map(({ group, items }) => {
                    const collapsed = collapsedGroups[group];
                    return (
                      <React.Fragment key={group}>
                        <ListItemButton
                          onClick={() => toggleGroup(group)}
                          sx={{ py: 0.25, minHeight: 30 }}
                        >
                          <ListItemText
                            primary={group}
                            primaryTypographyProps={{
                              variant: "overline",
                              fontWeight: 700,
                              fontSize: 10.5,
                              color: "text.secondary",
                            }}
                          />
                          {collapsed ? (
                            <ExpandMore fontSize="small" />
                          ) : (
                            <ExpandLess fontSize="small" />
                          )}
                        </ListItemButton>
                        <Collapse in={!collapsed} timeout="auto" unmountOnExit>
                          {items.map(({ tab, index }) => (
                            <ListItemButton
                              key={tab.label}
                              selected={chosenTab === index}
                              disabled={tab.disabled}
                              onClick={() => setTabValue(index)}
                              data-anchor={tab.dataAnchor}
                              sx={{
                                pl: 3,
                                py: 0.25,
                                minHeight: 30,
                                "&.Mui-selected": {
                                  color: "primary.main",
                                  borderRight: 2,
                                  borderColor: "primary.main",
                                },
                              }}
                            >
                              <ListItemText
                                primary={tab.label}
                                primaryTypographyProps={{
                                  fontSize: 13,
                                  fontWeight: chosenTab === index ? 600 : 400,
                                }}
                              />
                            </ListItemButton>
                          ))}
                        </Collapse>
                      </React.Fragment>
                    );
                  })}
                  {/* Sites and GRS are their own routes rather than tabs, so
                      they sit in the nav as links: the actions kebab is for
                      things that act on the org, not places to go. */}
                  <ListItemButton
                    onClick={() => toggleGroup("Places")}
                    sx={{ py: 0.25, minHeight: 30 }}
                  >
                    <ListItemText
                      primary="Places"
                      primaryTypographyProps={{
                        variant: "overline",
                        fontWeight: 700,
                        fontSize: 10.5,
                        color: "text.secondary",
                      }}
                    />
                    {collapsedGroups["Places"] ? (
                      <ExpandMore fontSize="small" />
                    ) : (
                      <ExpandLess fontSize="small" />
                    )}
                  </ListItemButton>
                  <Collapse
                    in={!collapsedGroups["Places"]}
                    timeout="auto"
                    unmountOnExit
                  >
                    {[
                      { label: "Sites", path: "sites" },
                      { label: "GRS", path: "grs" },
                    ].map(({ label, path }) => (
                      <ListItemButton
                        key={label}
                        component={Link}
                        to={{
                          pathname: `/organisation/${regionCode}/${roleId}/${path}`,
                          // The sites and GRS pages read the name from history
                          // state, as the kebab links have always passed it.
                          state: { orgName: orgData.name },
                        }}
                        data-testid={`org-nav-${path}`}
                        sx={{ pl: 3, py: 0.25, minHeight: 30 }}
                      >
                        <ListItemText
                          primary={label}
                          primaryTypographyProps={{ fontSize: 13 }}
                        />
                      </ListItemButton>
                    ))}
                  </Collapse>
                </List>
              </Paper>
              <Box sx={{ flex: 1, minWidth: 0 }}>{tabPanels}</Box>
            </Box>
          </Grid>
        ) : (
          <Grid item xs={12} lg={12}>
            <Tabs
              value={chosenTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
            >
              {tabs.map((tab) => (
                <Tab
                  label={tab.label}
                  key={tab.label}
                  disabled={tab.disabled}
                  data-anchor={tab.dataAnchor}
                />
              ))}
            </Tabs>
            {tabPanels}
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default Organisation;
